import React from "react";

import type { SvgIconComponent } from "@mui/icons-material";
// Conventional MUI icon passthroughs
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import AppsIcon from "@mui/icons-material/Apps";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlineIcon from "@mui/icons-material/LockOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import type { SvgIconProps } from "@mui/material";

// Custom domain icons
import { BuildIcon } from "./BuildIcon";
import { CacheIcon } from "./CacheIcon";
import { CloneIcon } from "./CloneIcon";
import { CloudIcon } from "./CloudIcon";
import { ConnectionIcon } from "./ConnectionIcon";
import { DashboardIcon } from "./DashboardIcon";
import { DatabaseIcon } from "./DatabaseIcon";
import { DeployIcon } from "./DeployIcon";
import { EnvironmentIcon } from "./EnvironmentIcon";
import { ErrorIcon } from "./ErrorIcon";
import { OrganizationIcon } from "./OrganizationIcon";
import { PendingIcon } from "./PendingIcon";
import { PipelineIcon } from "./PipelineIcon";
import { ProjectIcon } from "./ProjectIcon";
import { PushIcon } from "./PushIcon";
import { QueueIcon } from "./QueueIcon";
import { RunningIcon } from "./RunningIcon";
import { SecretIcon } from "./SecretIcon";
import { ServiceIcon } from "./ServiceIcon";
import { StackIcon } from "./StackIcon";
import { SuccessIcon } from "./SuccessIcon";
import { WarningIcon } from "./WarningIcon";

/**
 * Semantic icon names supported by the Rusha icon system.
 *
 * Domain icons are custom SVGs built with the dual-tone layered style.
 * Conventional icons are passthrough to MUI icons.
 */
export type RushaIconName =
  // Navigation & identity
  | "dashboard"
  | "apps"
  | "organization"
  | "project"
  | "stack"
  | "service"
  | "environment"
  // Resources
  | "database"
  | "cache"
  | "queue"
  | "secret"
  | "connection"
  // Pipeline & deployment
  | "pipeline"
  | "build"
  | "deploy"
  | "clone"
  | "push"
  | "cloud"
  // Status & feedback
  | "success"
  | "error"
  | "warning"
  | "pending"
  | "running"
  // Conventional (passthrough to MUI)
  | "search"
  | "copy"
  | "delete"
  | "close"
  | "settings"
  | "lock"
  | "visibility"
  | "download"
  | "refresh"
  | "save"
  | "clock"
  | "info"
  | "person"
  | "help"
  | "menu"
  | "arrowForward"
  | "arrowBack"
  | "arrowDropDown"
  | "check"
  | "add"
  | "edit"
  | "more"
  | "filter"
  | "expand"
  | "collapse";

/** Props for the RushaIcon component */
export interface RushaIconProps {
  name: RushaIconName;
  size?: number;
  sx?: SvgIconProps["sx"];
  className?: string;
}

/** Custom domain icon components */
const DOMAIN_ICONS: Record<string, React.FC<{ size?: number }>> = {
  dashboard: DashboardIcon,
  organization: OrganizationIcon,
  project: ProjectIcon,
  stack: StackIcon,
  service: ServiceIcon,
  environment: EnvironmentIcon,
  database: DatabaseIcon,
  cache: CacheIcon,
  queue: QueueIcon,
  secret: SecretIcon,
  connection: ConnectionIcon,
  pipeline: PipelineIcon,
  build: BuildIcon,
  deploy: DeployIcon,
  clone: CloneIcon,
  push: PushIcon,
  cloud: CloudIcon,
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  pending: PendingIcon,
  running: RunningIcon,
};

/** Conventional MUI icon passthroughs */
const CONVENTIONAL_ICONS: Partial<Record<RushaIconName, SvgIconComponent>> = {
  apps: AppsIcon,
  search: SearchIcon,
  copy: ContentCopyIcon,
  delete: DeleteOutlineIcon,
  close: CloseIcon,
  settings: SettingsIcon,
  lock: LockOutlineIcon,
  visibility: VisibilityOutlinedIcon,
  download: DownloadOutlinedIcon,
  refresh: RefreshIcon,
  save: SaveOutlinedIcon,
  clock: AccessTimeIcon,
  info: InfoOutlinedIcon,
  person: PersonOutlineIcon,
  help: HelpOutlineIcon,
  menu: MenuIcon,
  arrowForward: ArrowForwardIcon,
  arrowBack: ArrowBackIcon,
  arrowDropDown: ArrowDropDownIcon,
  check: CheckIcon,
  add: AddIcon,
  edit: EditOutlinedIcon,
  more: MoreHorizIcon,
  filter: FilterListIcon,
  expand: ExpandMoreIcon,
  collapse: UnfoldLessIcon,
};

/**
 * RushaIcon — Semantic icon registry.
 *
 * Renders custom Rusha domain icons or passes through to conventional MUI icons.
 *
 * Usage:
 *   <RushaIcon name="database" size={20} />
 *   <RushaIcon name="stack" />
 *   <RushaIcon name="search" />  // passthrough to MUI Search
 */
export const RushaIcon: React.FC<RushaIconProps> = ({
  name,
  size = 24,
  sx,
  className,
}) => {
  const DomainIcon = DOMAIN_ICONS[name];
  if (DomainIcon) {
    return <DomainIcon size={size} />;
  }

  const MuiIcon = CONVENTIONAL_ICONS[name];
  if (MuiIcon) {
    return <MuiIcon sx={{ fontSize: size, ...sx }} className={className} />;
  }

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[RushaIcon] Unknown icon name: "${name}"`);
  }
  return null;
};

RushaIcon.displayName = "RushaIcon";
